import type React from 'react';

import {
  AdornedInput,
  Adornment,
  ButtonGroup,
  FieldSet,
  Flex,
  FormField,
  FormHelp,
  Input,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
  ToggleButton,
} from '@zillow/constellation';
import { CurrencyInput, NumberStepper } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import { PARKING } from '../../../constants/index';

import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function Parking({ section, setSectionValues }: Props): JSX.Element {
  function onNoneClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.IS_NONE]: true,
      [PARKING.FIELD_NAME.IS_PROVIDED]: false,
    });
  }

  function onIncludedClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.IS_NONE]: false,
      [PARKING.FIELD_NAME.IS_PROVIDED]: true,
      [PARKING.FIELD_NAME.IS_INCLUDED]: true,
      [PARKING.FIELD_NAME.IS_ADDITIONAL_FEE]: false,
    });
  }

  function onAdditionalFeeClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.IS_NONE]: false,
      [PARKING.FIELD_NAME.IS_PROVIDED]: true,
      [PARKING.FIELD_NAME.IS_INCLUDED]: false,
      [PARKING.FIELD_NAME.IS_ADDITIONAL_FEE]: true,
    });
  }

  function onFeeAmountChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [PARKING.FIELD_NAME.ADDITIONAL_FEE_AMOUNT]: value });
  }

  function onAnySpotClick(_id: string): void {
    setSectionValues({
      [PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT]: true,
      [PARKING.FIELD_NAME.PROVIDED_IS_RESERVED]: false,
      [PARKING.FIELD_NAME.PROVIDED_RESERVED_SPACE]: '',
    });
  }

  function onReservedSpotClick(_id: string): void {
    setSectionValues({
      [PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT]: false,
      [PARKING.FIELD_NAME.PROVIDED_IS_RESERVED]: true,
    });
  }

  function onReservedSpotChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT]: false,
      [PARKING.FIELD_NAME.PROVIDED_IS_RESERVED]: true,
      [PARKING.FIELD_NAME.PROVIDED_RESERVED_SPACE]: value,
    });
  }

  function onVehicleCountChange(value: number): void {
    setSectionValues({
      [PARKING.FIELD_NAME.ADDITIONAL_FEE_HOW_MANY_VEHICLES]: value,
    });
  }

  const isNone = section.getFieldValue(PARKING.FIELD_NAME.IS_NONE);
  const isIncluded = section.getFieldValue(PARKING.FIELD_NAME.IS_INCLUDED);
  const isAdditionalFee = section.getFieldValue(PARKING.FIELD_NAME.IS_ADDITIONAL_FEE);

  return (
    <>
      <FormField
        label={<Label size="lg">Will the tenant be parking on the property?</Label>}
        control={
          <ButtonGroup direction="column" fluid marginBottom="sm" conjoined aria-label="tenant-parking">
            <ToggleButton onClick={onNoneClick} pressed={isNone}>
              No, none is available
            </ToggleButton>
            <ToggleButton onClick={onIncludedClick} pressed={isIncluded}>
              Yes, included in rent
            </ToggleButton>
            <ToggleButton onClick={onAdditionalFeeClick} pressed={isAdditionalFee}>
              Yes, for an additional fee
            </ToggleButton>
          </ButtonGroup>
        }
      />
      {isAdditionalFee && (
        <>
          <FormField
            marginBottom="sm"
            label={<Label>What is the total parking fee?</Label>}
            control={
              <AdornedInput
                leftAdornment={<Adornment>$</Adornment>}
                rightAdornment={<Adornment>per month</Adornment>}
                input={
                  <CurrencyInput
                    placeholder={CURRENCY_INPUT_PLACEHOLDER}
                    value={section.getFieldValue(PARKING.FIELD_NAME.ADDITIONAL_FEE_AMOUNT)}
                    onChange={onFeeAmountChange}
                    onBlur={onFeeAmountChange}
                  />
                }
              />
            }
          />
          <FormField
            marginBottom="sm"
            label={<Label>How many vehicles may be parked?</Label>}
            control={
              <Flex display="flex" alignItems="center" flexDirection="column">
                <NumberStepper
                  min={1}
                  max={5}
                  value={Number(section.getFieldValue(PARKING.FIELD_NAME.ADDITIONAL_FEE_HOW_MANY_VEHICLES))}
                  onChange={onVehicleCountChange}
                />
                <Paragraph>vehicles allowed</Paragraph>
              </Flex>
            }
          />
        </>
      )}
      {(isIncluded || isAdditionalFee) && (
        <>
          <FieldSet legend={<Legend>Where may the tenant park?</Legend>}>
            <LabeledControl
              controlId="LabeledControl-any-open-spot"
              label={<Label>Any open spot</Label>}
              control={
                <Radio
                  name="parking-option"
                  checked={section.getFieldValue(PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT)}
                  onChange={onAnySpotClick}
                />
              }
            />
            <LabeledControl
              controlId="LabeledControl-only-reserved-space"
              label={<Label>Only in reserved space(s)</Label>}
              control={
                <Radio
                  name="parking-option"
                  checked={section.getFieldValue(PARKING.FIELD_NAME.PROVIDED_IS_RESERVED)}
                  onChange={onReservedSpotClick}
                />
              }
            />
            <FormField
              marginLeft="lg"
              control={
                <Input
                  aria-label="reserved-spaces"
                  placeholder="(example: Spaces 10 and 11)"
                  onChange={onReservedSpotChange}
                  value={section.getFieldValue(PARKING.FIELD_NAME.PROVIDED_RESERVED_SPACE)}
                  onClick={onReservedSpotClick}
                />
              }
              description={<FormHelp>List the spaces for all vehicles</FormHelp>}
            />
          </FieldSet>
        </>
      )}
    </>
  );
}
